import React, { useCallback, useMemo, useState, useRef, createElement } from 'react';
import { Search, MessageSquare, AlertTriangle, CheckCircle2, Clock, Send, User, Paperclip, Plus, Star, Check, ShieldAlert, CreditCard, Zap, Download, AlignJustify, Pin, Lock, FileText, Shield, History, Info, X, CheckCheck, Tag, ChevronDown, ChevronRight, RefreshCw, Mail, Phone, AlertOctagon, ArrowLeft, ArrowUp } from 'lucide-react';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
interface Ticket {
  id: string;
  subject: string;
  user: string;
  priority: string;
  status: string;
  date: string;
}
interface AdminMessagingProps {
  tickets: Ticket[];
  setTickets: React.Dispatch<React.SetStateAction<Ticket[]>>;
  success: (msg: string) => void;
}
interface ChatMessage {
  id: string;
  sender: 'user' | 'admin' | 'system' | 'astrologer' | 'note';
  text: string;
  time: string;
  isRead?: boolean;
  attachments?: string[];
}
interface ExtendedTicket extends Ticket {
  avatar: string;
  type: 'support' | 'complaint' | 'refund' | 'tech' | 'ai' | 'finance';
  unread: number;
  isPinned: boolean;
  hasAttachment: boolean;
  slaStatus: 'ok' | 'warning' | 'breached';
  slaTime: string;
  riskLevel: 'low' | 'medium' | 'high';
  assignee: string | null;
  lastMessage: string;
  messages: ChatMessage[];
  isEscalated: boolean;
  isBlocked: boolean;
  hasRefund: boolean;
  userProfile: {
    email: string;
    phone: string;
    regDate: string;
    ltv: string;
    totalOrders: number;
    activeSubscription: string | null;
  };
}
const generateMockMessages = (ticket: Ticket): ChatMessage[] => {
  const isComplaint = ticket.subject.toLowerCase().includes('жалоб');
  const isRefund = ticket.subject.toLowerCase().includes('возврат');
  const messages: ChatMessage[] = [{
    id: 'm1',
    sender: 'system',
    text: `Тикет создан. Категория: ${isComplaint ? 'Жалоба' : isRefund ? 'Финансы' : 'Поддержка'}`,
    time: '10:00'
  }, {
    id: 'm2',
    sender: 'user',
    text: `Здравствуйте! У меня проблема: ${ticket.subject.toLowerCase()}. Помогите разобраться, пожалуйста.`,
    time: '10:05',
    attachments: ticket.id.endsWith('2') ? ['screenshot.png'] : undefined
  }];
  if (ticket.status === 'in_progress' || ticket.status === 'closed') {
    messages.push({
      id: 'm3',
      sender: 'admin',
      text: 'Добрый день! Взял ваш вопрос в работу. Сейчас всё проверим.',
      time: '10:15',
      isRead: true
    });
  }
  if (isComplaint) {
    messages.push({
      id: 'm4',
      sender: 'note',
      text: 'Внутренняя заметка: проверить историю заказов клиента, возможно повторная жалоба.',
      time: '10:20'
    });
  }
  if (ticket.status === 'closed') {
    messages.push({
      id: 'm5',
      sender: 'admin',
      text: 'Проблема решена. Если возникнут новые вопросы — обращайтесь!',
      time: '11:00',
      isRead: true
    });
    messages.push({
      id: 'm6',
      sender: 'system',
      text: 'Тикет закрыт администратором',
      time: '11:01'
    });
  }
  return messages;
};
const enrichTicket = (t: Ticket, index: number): ExtendedTicket => {
  const isComplaint = t.subject.toLowerCase().includes('жалоб');
  const isRefund = t.subject.toLowerCase().includes('возврат');
  const isTech = t.subject.toLowerCase().includes('ошибк') || t.subject.toLowerCase().includes('проблем');
  let type: ExtendedTicket['type'] = 'support';
  if (isComplaint) type = 'complaint';else if (isRefund) type = 'refund';else if (isTech) type = 'tech';else if (index % 5 === 0) type = 'ai';
  const slaMinutes = Math.floor(Math.random() * 180);
  let slaStatus: ExtendedTicket['slaStatus'] = 'ok';
  if (slaMinutes > 120) slaStatus = 'breached';else if (slaMinutes > 60) slaStatus = 'warning';
  return {
    ...t,
    avatar: t.user.substring(0, 2).toUpperCase(),
    type,
    unread: t.status === 'open' ? Math.floor(Math.random() * 3) + 1 : 0,
    isPinned: index === 0,
    hasAttachment: index % 3 === 0,
    slaStatus,
    slaTime: `${Math.floor(slaMinutes / 60)}ч ${slaMinutes % 60}м`,
    riskLevel: isComplaint ? 'high' : isRefund ? 'medium' : 'low',
    assignee: t.status !== 'open' ? 'Алексей (Admin)' : null,
    lastMessage: t.status === 'closed' ? 'Проблема решена.' : `У меня проблема: ${t.subject.toLowerCase()}...`,
    messages: generateMockMessages(t),
    isEscalated: false,
    isBlocked: false,
    hasRefund: false,
    userProfile: {
      email: `user${index}@example.com`,
      phone: '+7 (999) 000-00-00',
      regDate: '12.05.2023',
      ltv: `${(index * 1500 + 3000).toLocaleString('ru-RU')} \u20BD`,
      totalOrders: index % 5 + 1,
      activeSubscription: index % 2 === 0 ? 'Pro' : null
    }
  };
};
const quickTemplates = [{
  label: 'Приветствие',
  text: 'Здравствуйте! Спасибо за обращение. Сейчас разберёмся с вашим вопросом.'
}, {
  label: 'Запрос деталей',
  text: 'Пожалуйста, уточните детали: номер заказа и дату оплаты.'
}, {
  label: 'Решено',
  text: 'Проблема решена. Если есть ещё вопросы — пишите!'
}, {
  label: 'Ожидайте',
  text: 'Ваш запрос передан специалисту. Ответим в течение 1 часа.'
}, {
  label: 'Возврат',
  text: 'Возврат оформлен. Средства поступят в течение 3-5 рабочих дней.'
}, {
  label: 'Эскалация',
  text: 'Ваш вопрос передан старшему специалисту для детального рассмотрения.'
}];
const categoryTabs = [{
  id: 'all',
  label: 'Все'
}, {
  id: 'support',
  label: 'Поддержка'
}, {
  id: 'complaints',
  label: 'Жалобы'
}, {
  id: 'refunds',
  label: 'Возвраты'
}, {
  id: 'tech',
  label: 'Технические'
}, {
  id: 'ai',
  label: 'ИИ-отчёты'
}];
const ADMIN = 'Вы (Admin)';
const timeNow = () => new Date().toLocaleTimeString([], {
  hour: '2-digit',
  minute: '2-digit'
});
export function AdminMessaging({
  tickets,
  setTickets,
  success
}: AdminMessagingProps) {
  const [activeTab, setActiveTab] = useState('all');
  const [activeTicketId, setActiveTicketId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [compactMode, setCompactMode] = useState(false);
  const [showInfoPanel, setShowInfoPanel] = useState(true);
  const [messageText, setMessageText] = useState('');
  const [isInternalNote, setIsInternalNote] = useState(false);
  const [selectedTickets, setSelectedTickets] = useState<Set<string>>(new Set());
  const [metricFilter, setMetricFilter] = useState<string | null>(null);
  const [pinnedTickets, setPinnedTickets] = useState<Set<string>>(new Set(['TKT-200']));
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());
  const [extraMessages, setExtraMessages] = useState<Record<string, ChatMessage[]>>({});
  const [ticketFlags, setTicketFlags] = useState<Record<string, {
    isEscalated?: boolean;
    isBlocked?: boolean;
    hasRefund?: boolean;
  }>>({});
  const [showNewTicketForm, setShowNewTicketForm] = useState(false);
  const [newTicketSubject, setNewTicketSubject] = useState('');
  const [newTicketUser, setNewTicketUser] = useState('');
  const [newTicketPriority, setNewTicketPriority] = useState('medium');
  const [confirmAction, setConfirmAction] = useState<{
    label: string;
    action: () => void;
  } | null>(null);
  const [filters, setFilters] = useState({
    status: 'all',
    priority: 'all',
    type: 'all'
  });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);
  // Enrich tickets
  const extendedTickets = useMemo(() => tickets.map((t, i) => {
    const et = enrichTicket(t, i);
    const flags = ticketFlags[t.id];
    if (flags) {
      if (flags.isEscalated) et.isEscalated = true;
      if (flags.isBlocked) et.isBlocked = true;
      if (flags.hasRefund) et.hasRefund = true;
    }
    return et;
  }), [tickets, ticketFlags]);
  // Metrics
  const metrics = useMemo(() => ({
    total: extendedTickets.length,
    open: extendedTickets.filter(t => t.status === 'open').length,
    inProgress: extendedTickets.filter(t => t.status === 'in_progress').length,
    overdue: extendedTickets.filter(t => t.slaStatus === 'breached' && t.status !== 'closed').length,
    complaints: extendedTickets.filter(t => t.type === 'complaint').length,
    refunds: extendedTickets.filter(t => t.type === 'refund').length,
    noReply: extendedTickets.filter(t => t.status === 'open' && t.unread > 0).length,
    closed: extendedTickets.filter(t => t.status === 'closed').length
  }), [extendedTickets]);
  // Filtering
  const filteredTickets = useMemo(() => {
    let result = [...extendedTickets];
    if (metricFilter === 'open') result = result.filter(t => t.status === 'open');else if (metricFilter === 'inProgress') result = result.filter(t => t.status === 'in_progress');else if (metricFilter === 'overdue') result = result.filter(t => t.slaStatus === 'breached' && t.status !== 'closed');else if (metricFilter === 'complaints') result = result.filter(t => t.type === 'complaint');else if (metricFilter === 'refunds') result = result.filter(t => t.type === 'refund');else if (metricFilter === 'noReply') result = result.filter(t => t.status === 'open' && t.unread > 0);else if (metricFilter === 'closed') result = result.filter(t => t.status === 'closed');
    if (activeTab === 'support') result = result.filter(t => t.type === 'support');else if (activeTab === 'complaints') result = result.filter(t => t.type === 'complaint');else if (activeTab === 'refunds') result = result.filter(t => t.type === 'refund');else if (activeTab === 'tech') result = result.filter(t => t.type === 'tech');else if (activeTab === 'ai') result = result.filter(t => t.type === 'ai');
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(t => t.subject.toLowerCase().includes(q) || t.user.toLowerCase().includes(q) || t.id.toLowerCase().includes(q));
    }
    if (filters.status !== 'all') result = result.filter(t => t.status === filters.status);
    if (filters.priority !== 'all') result = result.filter(t => t.priority === filters.priority);
    if (filters.type !== 'all') result = result.filter(t => t.type === filters.type);
    return result.sort((a, b) => {
      if (pinnedTickets.has(a.id) && !pinnedTickets.has(b.id)) return -1;
      if (!pinnedTickets.has(a.id) && pinnedTickets.has(b.id)) return 1;
      if (a.unread > 0 && b.unread === 0) return -1;
      if (a.unread === 0 && b.unread > 0) return 1;
      return 0;
    });
  }, [extendedTickets, activeTab, searchQuery, filters, metricFilter, pinnedTickets]);
  const activeTicket = useMemo(() => extendedTickets.find(t => t.id === activeTicketId), [extendedTickets, activeTicketId]);
  const allMessages = activeTicket ? [...activeTicket.messages, ...(extraMessages[activeTicket.id] || [])] : [];
  // --- System message helper ---
  const addSystemMessage = useCallback((ticketId: string, text: string) => {
    setExtraMessages(prev => ({
      ...prev,
      [ticketId]: [...(prev[ticketId] || []), {
        id: `sys-${Date.now()}`,
        sender: 'system' as const,
        text,
        time: timeNow()
      }]
    }));
  }, []);
  // --- Handlers ---
  const handleSendMessage = useCallback(() => {
    if (!messageText.trim() || !activeTicketId) return;
    const newMsg: ChatMessage = {
      id: `msg-${Date.now()}`,
      sender: isInternalNote ? 'note' : 'admin',
      text: messageText,
      time: timeNow(),
      isRead: false
    };
    setExtraMessages(prev => ({
      ...prev,
      [activeTicketId]: [...(prev[activeTicketId] || []), newMsg]
    }));
    // Auto-set to in_progress if open
    setTickets(prev => prev.map(t => t.id === activeTicketId && t.status === 'open' ? {
      ...t,
      status: 'in_progress'
    } : t));
    success(isInternalNote ? 'Заметка добавлена' : 'Сообщение отправлено');
    setMessageText('');
    setIsInternalNote(false);
    setTimeout(() => chatEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    }), 100);
  }, [messageText, activeTicketId, isInternalNote, setTickets, success]);
  const handleCloseTicket = useCallback((id: string) => {
    setTickets(prev => prev.map(t => t.id === id ? {
      ...t,
      status: 'closed'
    } : t));
    addSystemMessage(id, 'Тикет закрыт администратором');
    success('Тикет закрыт');
  }, [setTickets, addSystemMessage, success]);
  const handleReopenTicket = useCallback((id: string) => {
    setTickets(prev => prev.map(t => t.id === id ? {
      ...t,
      status: 'open'
    } : t));
    addSystemMessage(id, 'Тикет переоткрыт администратором');
    success('Тикет переоткрыт');
  }, [setTickets, addSystemMessage, success]);
  const handleTakeTicket = useCallback((id: string) => {
    setTickets(prev => prev.map(t => t.id === id ? {
      ...t,
      status: 'in_progress'
    } : t));
    addSystemMessage(id, `Тикет назначен на ${ADMIN}`);
    success('Назначено на вас');
  }, [setTickets, addSystemMessage, success]);
  const handleEscalate = useCallback((id: string) => {
    setTickets(prev => prev.map(t => t.id === id ? {
      ...t,
      priority: 'high',
      status: t.status === 'open' ? 'in_progress' : t.status
    } : t));
    setTicketFlags(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        isEscalated: true
      }
    }));
    addSystemMessage(id, 'Тикет эскалирован руководству. Приоритет повышен до высокого.');
    success('Тикет эскалирован');
  }, [setTickets, addSystemMessage, success]);
  const handleRefund = useCallback((id: string) => {
    setTicketFlags(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        hasRefund: true
      }
    }));
    addSystemMessage(id, 'Возврат средств оформлен. Средства поступят в течение 3-5 рабочих дней.');
    setExtraMessages(prev => ({
      ...prev,
      [id]: [...(prev[id] || []), {
        id: `adm-${Date.now()}`,
        sender: 'admin' as const,
        text: 'Возврат оформлен. Средства поступят в течение 3-5 рабочих дней.',
        time: timeNow(),
        isRead: false
      }]
    }));
    success('Возврат оформлен');
  }, [addSystemMessage, success]);
  const handleBlockUser = useCallback((id: string, userName: string) => {
    setTicketFlags(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        isBlocked: true
      }
    }));
    addSystemMessage(id, `Пользователь ${userName} заблокирован администратором`);
    setTickets(prev => prev.map(t => t.id === id ? {
      ...t,
      status: 'closed'
    } : t));
    success(`Пользователь ${userName} заблокирован`);
    setConfirmAction(null);
  }, [setTickets, addSystemMessage, success]);
  const handleCreateTicket = useCallback(() => {
    if (!newTicketSubject.trim()) return;
    const newId = `TKT-${300 + tickets.length}`;
    setTickets(prev => [{
      id: newId,
      subject: newTicketSubject,
      user: newTicketUser || 'Администратор',
      priority: newTicketPriority,
      status: 'open',
      date: 'Сегодня'
    }, ...prev]);
    success(`Тикет ${newId} создан`);
    setNewTicketSubject('');
    setNewTicketUser('');
    setNewTicketPriority('medium');
    setShowNewTicketForm(false);
    setActiveTicketId(newId);
  }, [newTicketSubject, newTicketUser, newTicketPriority, tickets.length, setTickets, success]);
  const handleExport = useCallback(() => {
    const csvRows = ['ID,Пользователь,Тема,Статус,Приоритет,Дата'];
    filteredTickets.forEach(t => csvRows.push(`${t.id},${t.user},${t.subject},${t.status},${t.priority},${t.date}`));
    const blob = new Blob([csvRows.join('\n')], {
      type: 'text/csv;charset=utf-8;'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tickets_export_${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    success(`Экспортировано ${filteredTickets.length} тикетов`);
  }, [filteredTickets, success]);
  const togglePin = useCallback((id: string) => {
    setPinnedTickets(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        success('Откреплён');
      } else {
        next.add(id);
        success('Закреплён');
      }
      return next;
    });
  }, [success]);
  const toggleTicketSelection = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedTickets(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);else n.add(id);
      return n;
    });
  };
  const handleMassAction = useCallback((action: string) => {
    if (selectedTickets.size === 0) return;
    const ids = Array.from(selectedTickets);
    if (action === 'close') {
      setTickets(prev => prev.map(t => ids.includes(t.id) ? {
        ...t,
        status: 'closed'
      } : t));
      ids.forEach(id => addSystemMessage(id, 'Тикет закрыт (массовое действие)'));
    } else if (action === 'assign') {
      setTickets(prev => prev.map(t => ids.includes(t.id) && t.status === 'open' ? {
        ...t,
        status: 'in_progress'
      } : t));
      ids.forEach(id => addSystemMessage(id, `Назначен на ${ADMIN}`));
    }
    success(`${action === 'close' ? 'Закрыто' : 'Назначено'}: ${ids.length} тикетов`);
    setSelectedTickets(new Set());
  }, [selectedTickets, setTickets, addSystemMessage, success]);
  const handleFileAttach = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !activeTicketId) return;
    setExtraMessages(prev => ({
      ...prev,
      [activeTicketId]: [...(prev[activeTicketId] || []), {
        id: `msg-${Date.now()}`,
        sender: 'admin' as const,
        text: `\u{1F4CE} ${file.name} (${(file.size / 1024).toFixed(1)} KB)`,
        time: timeNow(),
        attachments: [file.name]
      }]
    }));
    e.target.value = '';
    success('Файл прикреплён');
  }, [activeTicketId, success]);
  const toggleSection = (s: string) => {
    setCollapsedSections(prev => {
      const n = new Set(prev);
      if (n.has(s)) n.delete(s);else n.add(s);
      return n;
    });
  };
  // --- Render helpers ---
  const getTypeIcon = (type: ExtendedTicket['type']) => {
    switch (type) {
      case 'complaint':
        return <ShieldAlert className="w-3.5 h-3.5 text-rose-500 flex-shrink-0" data-id="element-5757" />;
      case 'refund':
        return <CreditCard className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" data-id="element-5758" />;
      case 'tech':
        return <Zap className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" data-id="element-5759" />;
      case 'ai':
        return <Star className="w-3.5 h-3.5 text-purple-500 flex-shrink-0" data-id="element-5760" />;
      case 'finance':
        return <CreditCard className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" data-id="element-5761" />;
      default:
        return <MessageSquare className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" data-id="element-5762" />;
    }
  };
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'open':
        return <Badge variant="error" size="sm" data-id="element-5763">
            Новый
          </Badge>;
      case 'in_progress':
        return <Badge variant="warning" size="sm" data-id="element-5764">
            В работе
          </Badge>;
      case 'closed':
        return <Badge variant="neutral" size="sm" data-id="element-5765">
            Закрыт
          </Badge>;
      default:
        return <Badge variant="neutral" size="sm" data-id="element-5766">
            {status}
          </Badge>;
    }
  };
  const getSlaColor = (status: string) => {
    if (status === 'breached') return 'text-rose-600 bg-rose-50 border-rose-200';
    if (status === 'warning') return 'text-amber-600 bg-amber-50 border-amber-200';
    return 'text-emerald-600 bg-emerald-50 border-emerald-200';
  };
  const getRiskBadge = (level: string) => {
    if (level === 'high') return <span className="text-[10px] font-bold text-rose-600 bg-rose-50 px-1.5 py-0.5 rounded" data-id="element-5767">
          Высокий
        </span>;
    if (level === 'medium') return <span className="text-[10px] font-bold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded" data-id="element-5768">
          Средний
        </span>;
    return <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded" data-id="element-5769">
        Низкий
      </span>;
  };
  const isTerminal = (s: string) => s === 'closed';
  const isMobileChat = !!activeTicketId;
  const metricCards = [{
    key: null,
    label: 'Всего',
    value: metrics.total,
    icon: <MessageSquare className="w-4 h-4" data-id="element-5770" />,
    color: 'text-slate-600 bg-slate-50'
  }, {
    key: 'open',
    label: 'Открытые',
    value: metrics.open,
    icon: <AlertTriangle className="w-4 h-4" data-id="element-5771" />,
    color: 'text-rose-600 bg-rose-50'
  }, {
    key: 'inProgress',
    label: 'В работе',
    value: metrics.inProgress,
    icon: <Clock className="w-4 h-4" data-id="element-5772" />,
    color: 'text-blue-600 bg-blue-50'
  }, {
    key: 'overdue',
    label: 'Просрочено',
    value: metrics.overdue,
    icon: <AlertOctagon className="w-4 h-4" data-id="element-5773" />,
    color: 'text-rose-600 bg-rose-50'
  }, {
    key: 'noReply',
    label: 'Без ответа',
    value: metrics.noReply,
    icon: <Clock className="w-4 h-4" data-id="element-5774" />,
    color: 'text-amber-600 bg-amber-50'
  }, {
    key: 'complaints',
    label: 'Жалобы',
    value: metrics.complaints,
    icon: <ShieldAlert className="w-4 h-4" data-id="element-5775" />,
    color: 'text-orange-600 bg-orange-50'
  }, {
    key: 'refunds',
    label: 'Возвраты',
    value: metrics.refunds,
    icon: <CreditCard className="w-4 h-4" data-id="element-5776" />,
    color: 'text-purple-600 bg-purple-50'
  }, {
    key: 'closed',
    label: 'Закрыто',
    value: metrics.closed,
    icon: <CheckCircle2 className="w-4 h-4" data-id="element-5777" />,
    color: 'text-emerald-600 bg-emerald-50'
  }];
  return <div className="space-y-3 h-[calc(100vh-10rem)] min-h-[600px] flex flex-col" data-id="element-5778">
      {/* Confirm dialog */}
      {confirmAction && <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center" onClick={() => setConfirmAction(null)} data-id="element-5779">
          <div className="bg-white rounded-xl p-6 shadow-2xl max-w-sm w-full mx-4" onClick={e => e.stopPropagation()} data-id="element-5780">
            <h3 className="text-base font-bold text-slate-900 mb-2" data-id="element-5781">
              Подтверждение
            </h3>
            <p className="text-sm text-slate-600 mb-4" data-id="element-5782">
              Вы уверены: <strong data-id="element-5783">{confirmAction.label}</strong>?
            </p>
            <div className="flex gap-2 justify-end" data-id="element-5784">
              <Button variant="outline" size="sm" onClick={() => setConfirmAction(null)} data-id="element-5785">
                Отмена
              </Button>
              <Button variant="primary" size="sm" className="bg-rose-600 hover:bg-rose-700" onClick={confirmAction.action} data-id="element-5786">
                Подтвердить
              </Button>
            </div>
          </div>
        </div>}

      {/* New Ticket Form */}
      {showNewTicketForm && <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center" onClick={() => setShowNewTicketForm(false)} data-id="element-5787">
          <div className="bg-white rounded-xl p-6 shadow-2xl max-w-md w-full mx-4" onClick={e => e.stopPropagation()} data-id="element-5788">
            <div className="flex items-center justify-between mb-4" data-id="element-5789">
              <h3 className="text-base font-bold text-slate-900" data-id="element-5790">
                Новый тикет
              </h3>
              <button onClick={() => setShowNewTicketForm(false)} className="p-1 text-slate-400 hover:text-slate-600 rounded" data-id="element-5791">
                <X className="w-4 h-4" data-id="element-5792" />
              </button>
            </div>
            <div className="space-y-3" data-id="element-5793">
              <div data-id="element-5794">
                <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-5795">
                  Тема *
                </label>
                <input type="text" value={newTicketSubject} onChange={e => setNewTicketSubject(e.target.value)} placeholder="Опишите проблему..." className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-indigo-500" data-id="element-5796" />
              </div>
              <div data-id="element-5797">
                <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-5798">
                  Пользователь
                </label>
                <input type="text" value={newTicketUser} onChange={e => setNewTicketUser(e.target.value)} placeholder="Имя пользователя" className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-indigo-500" data-id="element-5799" />
              </div>
              <div data-id="element-5800">
                <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-5801">
                  Приоритет
                </label>
                <select value={newTicketPriority} onChange={e => setNewTicketPriority(e.target.value)} className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-indigo-500" data-id="element-5802">
                  <option value="low" data-id="element-5803">Низкий</option>
                  <option value="medium" data-id="element-5804">Средний</option>
                  <option value="high" data-id="element-5805">Высокий</option>
                </select>
              </div>
              <div className="flex gap-2 justify-end pt-2" data-id="element-5806">
                <Button variant="outline" size="sm" onClick={() => setShowNewTicketForm(false)} data-id="element-5807">
                  Отмена
                </Button>
                <Button variant="primary" size="sm" onClick={handleCreateTicket} disabled={!newTicketSubject.trim()} data-id="element-5808">
                  Создать
                </Button>
              </div>
            </div>
          </div>
        </div>}

      {/* Header */}
      <div className="flex items-center justify-between flex-shrink-0" data-id="element-5809">
        <div data-id="element-5810">
          <h2 className="text-xl font-bold text-slate-900" data-id="element-5811">
            Тикеты / Поддержка
          </h2>
          <p className="text-xs text-slate-500" data-id="element-5812">
            {filteredTickets.length} из {extendedTickets.length} тикетов
          </p>
        </div>
        <div className="flex items-center gap-2" data-id="element-5813">
          <Button variant="outline" size="sm" icon={<Download className="w-4 h-4" data-id="element-5815" />} onClick={handleExport} data-id="element-5814">
            Экспорт
          </Button>
          <Button variant="outline" size="sm" icon={<RefreshCw className="w-4 h-4" data-id="element-5817" />} onClick={() => {
          setExtraMessages({});
          setTicketFlags({});
          success('Данные обновлены');
        }} data-id="element-5816">
            Обновить
          </Button>
          <Button variant="primary" size="sm" icon={<Plus className="w-4 h-4" data-id="element-5819" />} onClick={() => setShowNewTicketForm(true)} data-id="element-5818">
            Новый тикет
          </Button>
        </div>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-4 xl:grid-cols-8 gap-2 flex-shrink-0" data-id="element-5820">
        {metricCards.map(m => <button key={m.label} onClick={() => setMetricFilter(metricFilter === m.key ? null : m.key)} className={`flex items-center gap-2 p-2 rounded-xl border transition-all text-left ${metricFilter === m.key ? 'border-indigo-300 bg-indigo-50 ring-1 ring-indigo-200' : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm'}`} data-id="element-5821">
            <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${m.color}`} data-id="element-5822">
              {m.icon}
            </div>
            <div className="min-w-0" data-id="element-5823">
              <div className="text-base font-bold text-slate-900 leading-none" data-id="element-5824">
                {m.value}
              </div>
              <div className="text-[9px] text-slate-500 truncate" data-id="element-5825">
                {m.label}
              </div>
            </div>
          </button>)}
      </div>

      {/* Tabs + Controls */}
      <div className="flex items-center gap-2 flex-shrink-0 flex-wrap" data-id="element-5826">
        <div className="flex gap-1 overflow-x-auto" data-id="element-5827">
          {categoryTabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`px-3 py-1.5 text-xs font-medium rounded-lg whitespace-nowrap transition-colors ${activeTab === tab.id ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-id="element-5828">
              {tab.label}
            </button>)}
        </div>
        <div className="flex-1" data-id="element-5829" />
        <div className="flex items-center gap-1.5" data-id="element-5830">
          <button onClick={() => setCompactMode(!compactMode)} className={`p-1.5 rounded-lg transition-colors ${compactMode ? 'bg-indigo-100 text-indigo-600' : 'text-slate-400 hover:bg-slate-100'}`} title="Компактный режим" data-id="element-5831">
            <AlignJustify className="w-4 h-4" data-id="element-5832" />
          </button>
          <button onClick={() => setShowFilters(!showFilters)} className={`p-1.5 rounded-lg transition-colors ${showFilters ? 'bg-indigo-100 text-indigo-600' : 'text-slate-400 hover:bg-slate-100'}`} title="Фильтры" data-id="element-5833">
            <Search className="w-4 h-4" data-id="element-5834" />
          </button>
          {selectedTickets.size > 0 && <div className="flex items-center gap-1 ml-2 pl-2 border-l border-slate-200" data-id="element-5835">
              <span className="text-xs text-slate-500" data-id="element-5836">
                {selectedTickets.size}
              </span>
              <Button variant="outline" size="sm" className="text-[10px] h-7 px-2" onClick={() => handleMassAction('assign')} data-id="element-5837">
                Назначить
              </Button>
              <Button variant="outline" size="sm" className="text-[10px] h-7 px-2" onClick={() => handleMassAction('close')} data-id="element-5838">
                Закрыть
              </Button>
              <Button variant="ghost" size="sm" className="text-[10px] h-7 px-2" onClick={() => setSelectedTickets(new Set())} data-id="element-5839">
                Снять
              </Button>
            </div>}
        </div>
      </div>

      {/* Filters */}
      {showFilters && <div className="flex items-center gap-3 p-3 bg-slate-50 border border-slate-200 rounded-xl flex-shrink-0 flex-wrap" data-id="element-5840">
          <select className="rounded-lg border border-slate-200 bg-white text-xs py-1.5 px-2" value={filters.status} onChange={e => setFilters({
        ...filters,
        status: e.target.value
      })} data-id="element-5841">
            <option value="all" data-id="element-5842">Статус: все</option>
            <option value="open" data-id="element-5843">Открыт</option>
            <option value="in_progress" data-id="element-5844">В работе</option>
            <option value="closed" data-id="element-5845">Закрыт</option>
          </select>
          <select className="rounded-lg border border-slate-200 bg-white text-xs py-1.5 px-2" value={filters.priority} onChange={e => setFilters({
        ...filters,
        priority: e.target.value
      })} data-id="element-5846">
            <option value="all" data-id="element-5847">Приоритет: все</option>
            <option value="high" data-id="element-5848">Высокий</option>
            <option value="medium" data-id="element-5849">Средний</option>
            <option value="low" data-id="element-5850">Низкий</option>
          </select>
          <select className="rounded-lg border border-slate-200 bg-white text-xs py-1.5 px-2" value={filters.type} onChange={e => setFilters({
        ...filters,
        type: e.target.value
      })} data-id="element-5851">
            <option value="all" data-id="element-5852">Тип: все</option>
            <option value="support" data-id="element-5853">Поддержка</option>
            <option value="complaint" data-id="element-5854">Жалоба</option>
            <option value="refund" data-id="element-5855">Возврат</option>
            <option value="tech" data-id="element-5856">Техническая</option>
            <option value="ai" data-id="element-5857">ИИ-отчёт</option>
          </select>
          <button onClick={() => {
        setFilters({
          status: 'all',
          priority: 'all',
          type: 'all'
        });
        setMetricFilter(null);
      }} className="text-xs text-indigo-600 hover:text-indigo-700 font-medium" data-id="element-5858">
            Сбросить
          </button>
        </div>}

      {/* Main 3-Column Layout */}
      <div className="flex-1 flex gap-0 overflow-hidden rounded-xl border border-slate-200 bg-white" data-id="element-5859">
        {/* LEFT: Ticket List */}
        <div className={`w-80 xl:w-96 flex-shrink-0 flex flex-col border-r border-slate-200 bg-slate-50/50 ${isMobileChat ? 'hidden lg:flex' : 'flex'}`} data-id="element-5860">
          <div className="p-2.5 border-b border-slate-200 bg-white flex-shrink-0" data-id="element-5861">
            <div className="relative" data-id="element-5862">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" data-id="element-5863" />
              <input type="text" placeholder="Поиск по тикетам..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-8 pr-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 bg-slate-50" data-id="element-5864" />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto" data-id="element-5865">
            {filteredTickets.length === 0 ? <div className="p-8 text-center text-slate-400" data-id="element-5866">
                <MessageSquare className="w-8 h-8 mx-auto mb-2 text-slate-300" data-id="element-5867" />
                <p className="text-sm" data-id="element-5868">Нет тикетов</p>
              </div> : filteredTickets.map(ticket => {
            const isPinned = pinnedTickets.has(ticket.id);
            const isSelected = selectedTickets.has(ticket.id);
            const flags = ticketFlags[ticket.id];
            return <div key={ticket.id} onClick={() => setActiveTicketId(ticket.id)} className={`border-b border-slate-100 cursor-pointer transition-colors ${activeTicketId === ticket.id ? 'bg-indigo-50/70 border-l-2 border-l-indigo-500' : 'hover:bg-slate-50'} ${isPinned ? 'bg-amber-50/30' : ''} ${isTerminal(ticket.status) ? 'opacity-60' : ''} ${compactMode ? 'px-3 py-2' : 'px-3 py-3'}`} data-id="element-5869">
                    <div className="flex items-start gap-2" data-id="element-5870">
                      <button onClick={e => toggleTicketSelection(ticket.id, e)} className={`mt-0.5 w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center transition-colors ${isSelected ? 'bg-indigo-600 border-indigo-600 text-white' : 'border-slate-300 hover:border-indigo-400'}`} data-id="element-5871">
                        {isSelected && <Check className="w-3 h-3" data-id="element-5872" />}
                      </button>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${ticket.type === 'complaint' ? 'bg-rose-100 text-rose-700' : ticket.type === 'refund' ? 'bg-amber-100 text-amber-700' : ticket.type === 'tech' ? 'bg-blue-100 text-blue-700' : 'bg-indigo-100 text-indigo-700'}`} data-id="element-5873">
                        {ticket.avatar}
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-5874">
                        <div className="flex items-center justify-between gap-1 mb-0.5" data-id="element-5875">
                          <div className="flex items-center gap-1.5 min-w-0" data-id="element-5876">
                            {isPinned && <Pin className="w-3 h-3 text-amber-500 flex-shrink-0" data-id="element-5877" />}
                            <span className="text-xs font-bold text-slate-900 truncate" data-id="element-5878">
                              {ticket.user}
                            </span>
                            {ticket.priority === 'high' && <span className="w-1.5 h-1.5 rounded-full bg-rose-500 flex-shrink-0" data-id="element-5879" />}
                          </div>
                          <span className="text-[10px] text-slate-400 whitespace-nowrap flex-shrink-0" data-id="element-5880">
                            {ticket.date}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5 mb-1" data-id="element-5881">
                          {getTypeIcon(ticket.type)}
                          <span className="text-xs font-medium text-slate-800 truncate" data-id="element-5882">
                            {ticket.subject}
                          </span>
                        </div>
                        {!compactMode && <p className="text-[11px] text-slate-500 truncate mb-1.5" data-id="element-5883">
                            {ticket.lastMessage}
                          </p>}
                        <div className="flex items-center gap-1.5 flex-wrap" data-id="element-5884">
                          {getStatusBadge(ticket.status)}
                          <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded border ${getSlaColor(ticket.slaStatus)}`} data-id="element-5885">
                            SLA: {ticket.slaTime}
                          </span>
                          {flags?.isEscalated && <span className="text-[9px] font-bold text-rose-600 bg-rose-50 px-1 rounded" data-id="element-5886">
                              ESC
                            </span>}
                          {flags?.hasRefund && <span className="text-[9px] font-bold text-purple-600 bg-purple-50 px-1 rounded" data-id="element-5887">
                              Возврат
                            </span>}
                          {flags?.isBlocked && <span className="text-[9px] font-bold text-slate-600 bg-slate-100 px-1 rounded" data-id="element-5888">
                              Блок
                            </span>}
                          {ticket.hasAttachment && <Paperclip className="w-3 h-3 text-slate-400 flex-shrink-0" data-id="element-5889" />}
                          {ticket.unread > 0 && <span className="bg-indigo-600 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full min-w-[16px] text-center" data-id="element-5890">
                              {ticket.unread}
                            </span>}
                        </div>
                      </div>
                    </div>
                  </div>;
          })}
          </div>
        </div>

        {/* CENTER: Chat */}
        <div className={`flex-1 flex flex-col min-w-0 ${!isMobileChat ? 'hidden lg:flex' : 'flex'}`} data-id="element-5891">
          {!activeTicket ? <div className="flex-1 flex flex-col items-center justify-center text-slate-400 bg-slate-50/30" data-id="element-5892">
              <MessageSquare className="w-12 h-12 text-slate-200 mb-3" data-id="element-5893" />
              <div className="text-sm font-medium" data-id="element-5894">Выберите тикет</div>
              <div className="text-xs text-slate-300 mt-1" data-id="element-5895">
                Нажмите на тикет слева
              </div>
            </div> : <>
              {/* Chat Header */}
              <div className="p-3 border-b border-slate-100 flex justify-between items-start flex-shrink-0 bg-white" data-id="element-5896">
                <div className="min-w-0 flex-1" data-id="element-5897">
                  <div className="flex items-center gap-2 mb-1 flex-wrap" data-id="element-5898">
                    <button onClick={() => setActiveTicketId(null)} className="lg:hidden p-1 text-slate-400 hover:text-slate-600 rounded flex-shrink-0" data-id="element-5899">
                      <ArrowLeft className="w-4 h-4" data-id="element-5900" />
                    </button>
                    <span className="text-xs font-mono text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded flex-shrink-0" data-id="element-5901">
                      {activeTicket.id}
                    </span>
                    <h3 className="text-sm font-bold text-slate-900 truncate" data-id="element-5902">
                      {activeTicket.subject}
                    </h3>
                    {getStatusBadge(activeTicket.status)}
                    {activeTicket.priority === 'high' && <Badge variant="error" size="sm" data-id="element-5903">
                        Высокий
                      </Badge>}
                    {ticketFlags[activeTicket.id]?.isEscalated && <Badge variant="error" size="sm" data-id="element-5904">
                        Эскалация
                      </Badge>}
                    {ticketFlags[activeTicket.id]?.hasRefund && <Badge variant="warning" size="sm" data-id="element-5905">
                        Возврат
                      </Badge>}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-slate-500 flex-wrap" data-id="element-5906">
                    <span className="flex items-center gap-1" data-id="element-5907">
                      <User className="w-3.5 h-3.5 flex-shrink-0" data-id="element-5908" />{' '}
                      {activeTicket.user}
                    </span>
                    <span className="flex items-center gap-1" data-id="element-5909">
                      <Clock className="w-3.5 h-3.5 flex-shrink-0" data-id="element-5910" />{' '}
                      {activeTicket.date}
                    </span>
                    {activeTicket.assignee && <span className="flex items-center gap-1" data-id="element-5911">
                        <Shield className="w-3.5 h-3.5 flex-shrink-0" data-id="element-5912" />{' '}
                        {activeTicket.assignee}
                      </span>}
                    <span className={`flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-medium ${getSlaColor(activeTicket.slaStatus)}`} data-id="element-5913">
                      SLA: {activeTicket.slaTime}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0 ml-2" data-id="element-5914">
                  <Button variant="outline" size="sm" className="text-[10px] h-7 px-2" onClick={() => togglePin(activeTicket.id)} data-id="element-5915">
                    <Pin className={`w-3.5 h-3.5 ${pinnedTickets.has(activeTicket.id) ? 'text-amber-500' : ''}`} data-id="element-5916" />
                  </Button>
                  {!isTerminal(activeTicket.status) ? <Button variant="outline" size="sm" className="text-[10px] h-7" icon={<CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" data-id="element-5918" />} onClick={() => handleCloseTicket(activeTicket.id)} data-id="element-5917">
                      Закрыть
                    </Button> : <Button variant="outline" size="sm" className="text-[10px] h-7" icon={<RefreshCw className="w-3.5 h-3.5" data-id="element-5920" />} onClick={() => handleReopenTicket(activeTicket.id)} data-id="element-5919">
                      Переоткрыть
                    </Button>}
                  {activeTicket.status === 'open' && !activeTicket.assignee && <Button variant="outline" size="sm" className="text-[10px] h-7" onClick={() => handleTakeTicket(activeTicket.id)} data-id="element-5921">
                      Взять
                    </Button>}
                  {!isTerminal(activeTicket.status) && !ticketFlags[activeTicket.id]?.isEscalated && <Button variant="outline" size="sm" className="text-[10px] h-7" onClick={() => handleEscalate(activeTicket.id)} data-id="element-5922">
                        <ArrowUp className="w-3.5 h-3.5" data-id="element-5923" />
                      </Button>}
                  <button onClick={() => setShowInfoPanel(!showInfoPanel)} className={`p-1.5 rounded-lg transition-colors ${showInfoPanel ? 'bg-indigo-100 text-indigo-600' : 'text-slate-400 hover:bg-slate-100'}`} data-id="element-5924">
                    <Info className="w-4 h-4" data-id="element-5925" />
                  </button>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="px-3 py-2 border-b border-slate-50 bg-slate-50/50 flex gap-1.5 overflow-x-auto flex-shrink-0" data-id="element-5926">
                <button onClick={() => {
              setShowInfoPanel(true);
              setCollapsedSections(p => {
                const n = new Set(p);
                n.delete('user');
                return n;
              });
            }} className="flex items-center gap-1 px-2 py-1 bg-white border border-slate-200 rounded-lg text-[10px] font-medium text-slate-600 hover:bg-slate-50 whitespace-nowrap flex-shrink-0" data-id="element-5927">
                  <User className="w-3 h-3" data-id="element-5928" /> Пользователь
                </button>
                {!isTerminal(activeTicket.status) && <>
                    {!ticketFlags[activeTicket.id]?.hasRefund && <button onClick={() => handleRefund(activeTicket.id)} className="flex items-center gap-1 px-2 py-1 bg-white border border-slate-200 rounded-lg text-[10px] font-medium text-slate-600 hover:bg-slate-50 whitespace-nowrap flex-shrink-0" data-id="element-5929">
                        <CreditCard className="w-3 h-3" data-id="element-5930" /> Возврат
                      </button>}
                    {!ticketFlags[activeTicket.id]?.isBlocked && <button onClick={() => setConfirmAction({
                label: `Блокировка ${activeTicket.user}`,
                action: () => handleBlockUser(activeTicket.id, activeTicket.user)
              })} className="flex items-center gap-1 px-2 py-1 bg-white border border-rose-200 rounded-lg text-[10px] font-medium text-rose-600 hover:bg-rose-50 whitespace-nowrap flex-shrink-0" data-id="element-5931">
                        <Shield className="w-3 h-3" data-id="element-5932" /> Блокировка
                      </button>}
                  </>}
                {ticketFlags[activeTicket.id]?.hasRefund && <span className="flex items-center gap-1 px-2 py-1 bg-purple-50 border border-purple-200 rounded-lg text-[10px] font-medium text-purple-600 whitespace-nowrap flex-shrink-0" data-id="element-5933">
                    <CheckCircle2 className="w-3 h-3" data-id="element-5934" /> Возврат оформлен
                  </span>}
                {ticketFlags[activeTicket.id]?.isBlocked && <span className="flex items-center gap-1 px-2 py-1 bg-rose-50 border border-rose-200 rounded-lg text-[10px] font-medium text-rose-600 whitespace-nowrap flex-shrink-0" data-id="element-5935">
                    <Shield className="w-3 h-3" data-id="element-5936" /> Заблокирован
                  </span>}
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50/30" data-id="element-5937">
                {allMessages.map(msg => {
              if (msg.sender === 'system') {
                return <div key={msg.id} className="flex justify-center" data-id="element-5938">
                        <div className="bg-slate-100 text-slate-500 text-[10px] px-3 py-1.5 rounded-full flex items-center gap-1.5 max-w-[90%]" data-id="element-5939">
                          <History className="w-3 h-3 flex-shrink-0" data-id="element-5940" />
                          <span className="truncate" data-id="element-5941">{msg.text}</span>
                          <span className="text-slate-400 ml-1 flex-shrink-0" data-id="element-5942">
                            {msg.time}
                          </span>
                        </div>
                      </div>;
              }
              if (msg.sender === 'note') {
                return <div key={msg.id} className="max-w-[85%] mx-auto" data-id="element-5943">
                        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3" data-id="element-5944">
                          <div className="flex items-center gap-1.5 mb-1" data-id="element-5945">
                            <Lock className="w-3 h-3 text-amber-600 flex-shrink-0" data-id="element-5946" />
                            <span className="text-[10px] font-bold text-amber-700" data-id="element-5947">
                              Внутренняя заметка
                            </span>
                            <span className="text-[9px] text-amber-500 ml-auto flex-shrink-0" data-id="element-5948">
                              {msg.time}
                            </span>
                          </div>
                          <p className="text-xs text-amber-800 break-words" data-id="element-5949">
                            {msg.text}
                          </p>
                        </div>
                      </div>;
              }
              const isOwn = msg.sender === 'admin';
              return <div key={msg.id} className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`} data-id="element-5950">
                      <div className="flex items-end gap-2 max-w-[80%]" data-id="element-5951">
                        {!isOwn && <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${msg.sender === 'astrologer' ? 'bg-purple-100 text-purple-700' : 'bg-indigo-100 text-indigo-700'}`} data-id="element-5952">
                            {msg.sender === 'astrologer' ? 'АС' : activeTicket.avatar}
                          </div>}
                        <div data-id="element-5953">
                          <div className={`px-3 py-2 rounded-2xl ${isOwn ? 'bg-indigo-600 text-white rounded-br-sm' : 'bg-white border border-slate-200 text-slate-800 rounded-bl-sm shadow-sm'}`} data-id="element-5954">
                            <p className="text-xs break-words" data-id="element-5955">{msg.text}</p>
                            {msg.attachments && msg.attachments.length > 0 && <div className={`flex items-center gap-1.5 mt-1.5 pt-1.5 border-t ${isOwn ? 'border-indigo-500' : 'border-slate-100'}`} data-id="element-5956">
                                <Paperclip className={`w-3 h-3 flex-shrink-0 ${isOwn ? 'text-indigo-300' : 'text-slate-400'}`} data-id="element-5957" />
                                <span className={`text-[10px] truncate ${isOwn ? 'text-indigo-200' : 'text-indigo-600'}`} data-id="element-5958">
                                  {msg.attachments[0]}
                                </span>
                              </div>}
                          </div>
                          <div className={`flex items-center gap-1 mt-0.5 ${isOwn ? 'justify-end mr-1' : 'ml-1'}`} data-id="element-5959">
                            <span className="text-[9px] text-slate-400" data-id="element-5960">
                              {msg.time}
                            </span>
                            {isOwn && msg.isRead && <CheckCheck className="w-3 h-3 text-indigo-400" data-id="element-5961" />}
                          </div>
                        </div>
                      </div>
                    </div>;
            })}
                <div ref={chatEndRef} data-id="element-5962" />
              </div>

              {/* Input */}
              {!isTerminal(activeTicket.status) && <div className="border-t border-slate-100 bg-white flex-shrink-0" data-id="element-5963">
                  <div className="px-3 pt-2 flex gap-1.5 overflow-x-auto" data-id="element-5964">
                    {quickTemplates.map(t => <button key={t.label} onClick={() => setMessageText(t.text)} className="px-2 py-1 bg-slate-100 hover:bg-indigo-50 hover:text-indigo-700 text-slate-600 text-[10px] font-medium rounded-lg whitespace-nowrap transition-colors flex-shrink-0" data-id="element-5965">
                        {t.label}
                      </button>)}
                  </div>
                  <div className="p-3 flex items-end gap-2" data-id="element-5966">
                    <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileAttach} data-id="element-5967" />
                    <button onClick={() => fileInputRef.current?.click()} className="p-2 text-slate-400 hover:text-indigo-600 rounded-lg hover:bg-slate-100 transition-colors flex-shrink-0" data-id="element-5968">
                      <Paperclip className="w-4 h-4" data-id="element-5969" />
                    </button>
                    <button onClick={() => setIsInternalNote(!isInternalNote)} className={`p-2 rounded-lg transition-colors flex-shrink-0 ${isInternalNote ? 'bg-amber-100 text-amber-600' : 'text-slate-400 hover:text-amber-600 hover:bg-slate-100'}`} title="Внутренняя заметка" data-id="element-5970">
                      <Lock className="w-4 h-4" data-id="element-5971" />
                    </button>
                    <div className="flex-1 min-w-0" data-id="element-5972">
                      <textarea value={messageText} onChange={e => setMessageText(e.target.value)} placeholder={isInternalNote ? 'Внутренняя заметка (не видна клиенту)...' : 'Ответ пользователю...'} className={`w-full rounded-xl border p-2.5 text-xs resize-none min-h-[40px] max-h-[120px] focus:outline-none focus:ring-1 ${isInternalNote ? 'border-amber-300 bg-amber-50/50 focus:border-amber-500 focus:ring-amber-500' : 'border-slate-200 bg-slate-50 focus:border-indigo-500 focus:ring-indigo-500'}`} rows={1} onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }} data-id="element-5973" />
                    </div>
                    <Button variant="primary" className="h-10 w-10 rounded-xl flex items-center justify-center p-0 flex-shrink-0" disabled={!messageText.trim()} onClick={handleSendMessage} data-id="element-5974">
                      <Send className="w-4 h-4" data-id="element-5975" />
                    </Button>
                  </div>
                </div>}
            </>}
        </div>

        {/* RIGHT: Info Panel */}
        {showInfoPanel && activeTicket && <div className="w-72 flex-shrink-0 border-l border-slate-200 bg-white overflow-y-auto hidden xl:block" data-id="element-5976">
            <div className="p-4 border-b border-slate-100" data-id="element-5977">
              <button onClick={() => toggleSection('user')} className="flex items-center justify-between w-full mb-3" data-id="element-5978">
                <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider" data-id="element-5979">
                  Пользователь
                </h4>
                {collapsedSections.has('user') ? <ChevronRight className="w-3.5 h-3.5 text-slate-400" data-id="element-5980" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" data-id="element-5981" />}
              </button>
              {!collapsedSections.has('user') && <div className="space-y-2.5" data-id="element-5982">
                  <div className="flex items-center gap-3" data-id="element-5983">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${activeTicket.type === 'complaint' ? 'bg-rose-100 text-rose-700' : 'bg-indigo-100 text-indigo-700'}`} data-id="element-5984">
                      {activeTicket.avatar}
                    </div>
                    <div className="min-w-0" data-id="element-5985">
                      <div className="text-sm font-bold text-slate-900 truncate" data-id="element-5986">
                        {activeTicket.user}
                      </div>
                      <div className="text-[10px] text-slate-500 truncate" data-id="element-5987">
                        {activeTicket.userProfile.email}
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2" data-id="element-5988">
                    <div className="text-center p-2 bg-slate-50 rounded-lg" data-id="element-5989">
                      <div className="text-sm font-bold text-slate-900" data-id="element-5990">
                        {activeTicket.userProfile.totalOrders}
                      </div>
                      <div className="text-[9px] text-slate-500" data-id="element-5991">Заказов</div>
                    </div>
                    <div className="text-center p-2 bg-slate-50 rounded-lg" data-id="element-5992">
                      <div className="text-sm font-bold text-slate-900 truncate" data-id="element-5993">
                        {activeTicket.userProfile.ltv}
                      </div>
                      <div className="text-[9px] text-slate-500" data-id="element-5994">LTV</div>
                    </div>
                  </div>
                  <div className="space-y-1.5" data-id="element-5995">
                    <div className="flex items-center gap-2 text-xs text-slate-600" data-id="element-5996">
                      <Phone className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" data-id="element-5997" />
                      <span className="truncate" data-id="element-5998">
                        {activeTicket.userProfile.phone}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-slate-600" data-id="element-5999">
                      <Mail className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" data-id="element-6000" />
                      <span className="truncate" data-id="element-6001">
                        {activeTicket.userProfile.email}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-slate-600" data-id="element-6002">
                      <Clock className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" data-id="element-6003" />
                      Рег: {activeTicket.userProfile.regDate}
                    </div>
                    {activeTicket.userProfile.activeSubscription && <div className="flex items-center gap-2 text-xs" data-id="element-6004">
                        <Tag className="w-3.5 h-3.5 text-indigo-400 flex-shrink-0" data-id="element-6005" />
                        <span className="text-indigo-600 font-medium" data-id="element-6006">
                          {activeTicket.userProfile.activeSubscription}
                        </span>
                      </div>}
                  </div>
                </div>}
            </div>

            <div className="p-4 border-b border-slate-100" data-id="element-6007">
              <button onClick={() => toggleSection('risk')} className="flex items-center justify-between w-full mb-3" data-id="element-6008">
                <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider" data-id="element-6009">
                  Риск и SLA
                </h4>
                {collapsedSections.has('risk') ? <ChevronRight className="w-3.5 h-3.5 text-slate-400" data-id="element-6010" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" data-id="element-6011" />}
              </button>
              {!collapsedSections.has('risk') && <div className="space-y-2" data-id="element-6012">
                  <div className="flex items-center justify-between" data-id="element-6013">
                    <span className="text-xs text-slate-500" data-id="element-6014">
                      Уровень риска
                    </span>
                    {getRiskBadge(activeTicket.riskLevel)}
                  </div>
                  <div className="flex items-center justify-between" data-id="element-6015">
                    <span className="text-xs text-slate-500" data-id="element-6016">SLA статус</span>
                    <span className={`text-[10px] font-medium px-2 py-0.5 rounded border ${getSlaColor(activeTicket.slaStatus)}`} data-id="element-6017">
                      {activeTicket.slaTime}
                    </span>
                  </div>
                  <div className="flex items-center justify-between" data-id="element-6018">
                    <span className="text-xs text-slate-500" data-id="element-6019">Тип</span>
                    <div className="flex items-center gap-1" data-id="element-6020">
                      {getTypeIcon(activeTicket.type)}
                      <span className="text-xs text-slate-700" data-id="element-6021">
                        {activeTicket.type}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between" data-id="element-6022">
                    <span className="text-xs text-slate-500" data-id="element-6023">
                      Ответственный
                    </span>
                    <span className="text-xs text-slate-700" data-id="element-6024">
                      {activeTicket.assignee || '—'}
                    </span>
                  </div>
                </div>}
            </div>

            <div className="p-4" data-id="element-6025">
              <button onClick={() => toggleSection('actions')} className="flex items-center justify-between w-full mb-3" data-id="element-6026">
                <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider" data-id="element-6027">
                  Действия
                </h4>
                {collapsedSections.has('actions') ? <ChevronRight className="w-3.5 h-3.5 text-slate-400" data-id="element-6028" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" data-id="element-6029" />}
              </button>
              {!collapsedSections.has('actions') && <div className="space-y-1.5" data-id="element-6030">
                  {!isTerminal(activeTicket.status) && !ticketFlags[activeTicket.id]?.hasRefund && <button onClick={() => handleRefund(activeTicket.id)} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-amber-700 hover:bg-amber-50 rounded-lg transition-colors" data-id="element-6031">
                        <CreditCard className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" data-id="element-6032" />{' '}
                        Оформить возврат
                      </button>}
                  {ticketFlags[activeTicket.id]?.hasRefund && <div className="w-full flex items-center gap-2 px-3 py-2 text-xs text-purple-600 bg-purple-50 rounded-lg" data-id="element-6033">
                      <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" data-id="element-6034" />{' '}
                      Возврат оформлен
                    </div>}
                  {!isTerminal(activeTicket.status) && !ticketFlags[activeTicket.id]?.isEscalated && <button onClick={() => handleEscalate(activeTicket.id)} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-orange-700 hover:bg-orange-50 rounded-lg transition-colors" data-id="element-6035">
                        <ArrowUp className="w-3.5 h-3.5 text-orange-500 flex-shrink-0" data-id="element-6036" />{' '}
                        Эскалировать
                      </button>}
                  {ticketFlags[activeTicket.id]?.isEscalated && <div className="w-full flex items-center gap-2 px-3 py-2 text-xs text-rose-600 bg-rose-50 rounded-lg" data-id="element-6037">
                      <ArrowUp className="w-3.5 h-3.5 flex-shrink-0" data-id="element-6038" />{' '}
                      Эскалирован
                    </div>}
                  {!isTerminal(activeTicket.status) && !ticketFlags[activeTicket.id]?.isBlocked && <button onClick={() => setConfirmAction({
              label: `Блокировка ${activeTicket.user}`,
              action: () => handleBlockUser(activeTicket.id, activeTicket.user)
            })} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-rose-700 hover:bg-rose-50 rounded-lg transition-colors" data-id="element-6039">
                        <Shield className="w-3.5 h-3.5 text-rose-500 flex-shrink-0" data-id="element-6040" />{' '}
                        Заблокировать
                      </button>}
                  {ticketFlags[activeTicket.id]?.isBlocked && <div className="w-full flex items-center gap-2 px-3 py-2 text-xs text-slate-600 bg-slate-100 rounded-lg" data-id="element-6041">
                      <Shield className="w-3.5 h-3.5 flex-shrink-0" data-id="element-6042" />{' '}
                      Заблокирован
                    </div>}
                </div>}
            </div>
          </div>}
      </div>
    </div>;
}